import { useCallback, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { getMachineImageData, ImageUpload } from '@helpers';
import { DOCUMENT_TYPE_TAG_KEY_MAP, KEYS_TAGS } from '@components/imageHandling/common';
import { IMAGE_UPLOAD_HEADER_TEXT } from '@v2/pages/common/constants/base.constants';
import { useBuildLinkListByHeaderConfigAndNavigationContext } from '@v2/pages/common/hooks/useBuildLinkListByHeaderConfigAndNavigationContext';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { generateImageURL } from '@reducers/blobStorage';

import type { ImagesProps } from '@components/machines/components/detailsComponents/activeMachine/components/Images';

export interface UseManageImagesParams
  extends Pick<
    ImagesProps,
    | 'machine'
    | 'machineId'
    | 'documentRetrieveKey'
    | 'documentRetrieveId'
    | 'images'
    | 'largeAddView'
    | 'panelViewMode'
    | 'onlySelectImage'
    | 'uploadImage'
    | 'preselectedScopeIds'
  > {}

export const useManageImages = (params: UseManageImagesParams) => {
  const {
    machine,
    machineId,
    documentRetrieveKey,
    documentRetrieveId,
    images,
    largeAddView,
    panelViewMode,
    onlySelectImage,
    uploadImage,
    preselectedScopeIds,
  } = params;

  const dispatch = useAppDispatch();
  const imageDocumentReferences = useBuildLinkListByHeaderConfigAndNavigationContext();

  const [machineImageLink, setMachineImageLink] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      (async () => {
        if (!machine?.machineImage) return;

        const { machineImage } = machine ?? {};

        if (machineImage?.local && machineImage?.url) {
          setMachineImageLink(machineImage.url);
          return;
        }

        const imageLink = await dispatch(
          generateImageURL({
            size: '_1x',
            cropped: machineImage?.isCropped,
            item: { imageHash: machineImage?.contentHash, extensionDerivative: machineImage?.extensionDerivative },
            page: machineImage?.pageNumber,
          })
        );

        setMachineImageLink(imageLink);
      })();
    }, [machineId, machine?.machineImage?.documentId])
  );

  const shouldDisplayMachineImage = !!(machineId && machine?.machineImage && machineImageLink);
  const noImages = !shouldDisplayMachineImage && !images?.length && (largeAddView || panelViewMode);
  const showFlatLine = !images?.length && !panelViewMode && !shouldDisplayMachineImage;
  const machineImageData = getMachineImageData(machine);

  const upload = () => {
    const link = {
      scopeId: machineId != null ? KEYS_TAGS['Machine'] : DOCUMENT_TYPE_TAG_KEY_MAP[documentRetrieveKey],
      scopeReferenceId:
        machineId != null ? machineId : Number.isFinite(+documentRetrieveId) ? Number(documentRetrieveId) : documentRetrieveId,
      isFavorite: machineId != null,
    };

    const tags = preselectedScopeIds
      ? imageDocumentReferences.filter((r) => preselectedScopeIds.includes(r.scopeId))
      : [link];

    ImageUpload({
      ...(machineId != null ? { headerText: IMAGE_UPLOAD_HEADER_TEXT.AddToMachine } : {}),
      saveToMainStore: true,
      saveToEnd: false,
      onlySelectImage,
      uploadStart: (img: unknown) => {
        if (uploadImage && onlySelectImage) {
          uploadImage(img);
        }
      },
      imageDocumentReferences,
      tags,
    });
  };

  return {
    machineImageLink,
    shouldDisplayMachineImage,
    noImages,
    showFlatLine,
    machineImageData,
    upload,
  };
};
